import StyledButton from 'components/general/StyledButton';
import StyledDivider from 'components/general/StyledDivider';
import { memo, useContext, useEffect, useRef, useState } from 'react';
import { useReactFlow } from 'react-flow-renderer';
import { RoutineContext, useRoutineMenuConfig } from '.';
import RoutineForm from '../RoutineForms';
import { LogicalConfigurationForm } from '../RoutineForms/CombinationalLogicForm';
import { StateTransitionForm } from '../RoutineForms/FiniteStateMachineForm';
import AddMenu from './AddMenu';
import useStyles from './styles';

export const ViewOnlyMenu = ({
  selectEntity,
  fsmEdit,
}: {
  selectEntity: (id: string | null) => void;
  fsmEdit: string | null;
}) => {
  const { classes } = useStyles();
  return fsmEdit ? (
    <div className={classes.editMenu}>
      <div className={classes.menuHeader}>
        <StyledButton onClick={() => selectEntity(null)} dontDisableInReadOnly>
          <h6>Back</h6>
        </StyledButton>
      </div>
    </div>
  ) : null;
};

const EditMenu = ({ selectEntity }: { selectEntity: (id: string | null) => void }) => {
  const [config] = useRoutineMenuConfig();
  const { classes } = useStyles();
  const { state, deleteBlock } = useContext(RoutineContext);
  const { setNodes, setEdges } = useReactFlow();
  const [dims, setDims] = useState({ width: 0, height: 0 });
  const contentRef = useRef<HTMLDivElement>(null);
  const [openAddMenu, setOpenAddMenu] = useState(false);

  useEffect(() => {
    if (contentRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        setDims({
          width: entries[0].contentRect.width,
          height: entries[0].contentRect.height,
        });
      });
      resizeObserver.observe(contentRef.current);
      return () => resizeObserver.disconnect();
    }
  }, []);

  return (
    <div className={classes.editMenu} style={dims}>
      <div ref={contentRef} className={classes.menuContent}>
        <div className={classes.menuHeader}>
          <div>
            <StyledButton
              onClick={() => {
                if (config.open) {
                  selectEntity(null);
                  setOpenAddMenu(false);
                } else setOpenAddMenu((prev) => !prev);
              }}
            >
              <h6 style={{ whiteSpace: 'nowrap' }}>
                {openAddMenu || config.open ? 'Back' : 'Add Routines'}
              </h6>
            </StyledButton>
            {config.open && (
              <StyledButton
                onClick={() => {
                  config.ids.forEach((id) => deleteBlock(id));
                  setNodes((nds) => nds.filter((n) => !config.ids.includes(n.id)));
                  setEdges((eds) =>
                    eds.filter((e) => {
                      const ids = e.id.split('-');
                      return !config.ids.includes(ids[0]) && !config.ids.includes(ids[1]);
                    })
                  );
                  selectEntity(null);
                }}
              >
                <h6>Delete</h6>
              </StyledButton>
            )}
          </div>
          {(config.open || openAddMenu) && <StyledDivider style={{ width: '100%', margin: 0 }} />}
        </div>
        {config.open && (
          <div className={classes.forms}>
            <div style={{ padding: '5px 10px' }}>
              {config.ids.map((id) =>
                state.blocks[id] ? (
                  state.blocks[id].type === 'LogicalConfiguration' ? (
                    <LogicalConfigurationForm key={id} id={id} />
                  ) : state.blocks[id].type === 'StateTransition' ? (
                    <StateTransitionForm key={id} id={id} />
                  ) : (
                    <RoutineForm key={id} id={id} />
                  )
                ) : null
              )}
            </div>
          </div>
        )}
        {!config.open && openAddMenu && <AddMenu />}
      </div>
    </div>
  );
};

export default memo(EditMenu);
